<template>
  <div
    class="input-wrapper mb-4"
    :class="{ active: isActive, disabled: props.disabled }"
    @click="handleClickWrapper">
    <input
      ref="input"
      class="input-field"
      type="text"
      :value="inputValue"
      :disabled="props.disabled"
      :name="props.name"
      :autocomplete="props.autoComplete"
      :inputmode="props.inputMode || 'text'"
      :required="isRequired"
      @input="handleInput"
      @focus="isFocused = true"
      @blur="handleBlur"
      @keypress="handleKeypress"
      @paste="handlePaste" />
    <div v-if="showRequiredText" class="required-text">
      {{ t('AtomInput.required') }}
    </div>

    <div class="placeholder">{{ placeholder }}</div>
    <div class="error-message">{{ errorMessage }}</div>
    <div
      v-if="props.infoMessage"
      class="info-icon"
      :class="{ active: showingInfoBox }"
      @mouseover="showingInfoBox = true"
      @mouseleave="showingInfoBox = false"
      @click.stop="showingInfoBox = !showingInfoBox">
      ?
    </div>
    <div v-if="showingInfoBox" class="info">
      {{ props.infoMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { AutocompleteTypes } from '~/types/enums/autocomplete.enum';
import { InputMode } from '~/types/enums/inputMode.enum';
import { phoneInputRegex } from '@/utils/regex';

interface Props {
  placeholder?: string;
  name?: string;
  autoComplete?: AutocompleteTypes;
  inputMode?: InputMode | undefined;
  modelValue: string | null;
  errorMessage?: string | null;
  infoMessage?: string | null;
  check?: 'phoneNumber' | ((value: string) => boolean) | null;
  disabled?: boolean;
  isRequired?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  name: '',
  autoComplete: AutocompleteTypes.Off,
  inputMode: InputMode.Text,
  modelValue: '',
  errorMessage: null,
  infoMessage: null,
  check: null
});

const { t } = useI18n();
const showingInfoBox = ref(false);

const inputValue = ref(props.modelValue || '');
const isDirty = ref(false);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== null && val !== undefined) {
      inputValue.value = val;
    }
  }
);

const emit = defineEmits(['update:modelValue', 'blur']);

const isFocused = ref(false);

const isActive = computed<boolean>(() => !!inputValue.value.length);

const input = ref<HTMLInputElement | null>(null);

function handleClickWrapper(event: Event) {
  if (props.disabled) {
    event.stopPropagation();
    return;
  }
  input.value?.focus();
}

const check: ((val: string) => boolean) | null = (() => {
  if (props.check === 'phoneNumber') {
    return (val: string) => {
      if (val.length < 3) {
        return /^\d+$/.test(val) || ['+', '+4'].includes(val);
      }
      return phoneInputRegex.test(val);
    };
  } else if (props.check) {
    return props.check;
  } else {
    return null;
  }
})();

function handleKeypress(event: KeyboardEvent) {
  if (event.code === 'Enter' || event.code === 'NumpadEnter' || event.metaKey) {
    return;
  }
  const target = event.target as HTMLInputElement;
  const selection = window.getSelection()?.toString();
  let newValue;
  if (
    selection &&
    target.selectionStart !== null &&
    target.selectionEnd !== null
  ) {
    newValue =
      target.value.substring(0, target.selectionStart) +
      event.key +
      target.value.substring(target.selectionEnd);
  } else {
    newValue = target.value + event.key;
  }
  if (check && !check(newValue)) {
    event.preventDefault();
  }
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  isDirty.value = true;
  emit('update:modelValue', inputValue.value);
}

function handlePaste(event: ClipboardEvent) {
  event.preventDefault();

  const pastedText = event.clipboardData?.getData('text') || '';
  const target = event.target as HTMLInputElement;

  if (check && !check(pastedText)) {
    return;
  }

  inputValue.value = pastedText;
  target.value = pastedText;
  isDirty.value = true;
  emit('update:modelValue', inputValue.value);
}

function handleBlur() {
  emit('blur', input.value?.value);
  isFocused.value = false;
}

const showRequiredText = computed(
  () => props.isRequired && isDirty.value && !inputValue.value
);
</script>

<style lang="scss" scoped>
.input-wrapper {
  position: relative;

  &.disabled * {
    cursor: not-allowed !important;
  }
}

.input-field {
  width: 100%;
  box-sizing: border-box;
  padding: $input-component-padding;
  font-size: font-size('sm');
  border: border($input-border);
  border-radius: $input-border-radius;
  box-shadow: $theme-box-shadow;
  font-weight: font-weight('semibold');

  @media (hover) {
    &:hover {
      background: $color-gray-100;
    }
  }

  &:focus {
    outline: none;
  }

  &:active {
    opacity: 1;
  }

  &:disabled {
    background-color: white !important;
  }

  &:-webkit-autofill,
  &:focus,
  .input-wrapper.active & {
    padding-top: calc(1.3 * $input-component-padding + 0.05rem);
    padding-bottom: calc(0.7 * $input-component-padding - 0.05rem);
  }
}

.placeholder {
  position: absolute;
  z-index: 100;
  top: $input-component-padding;
  left: 2.5rem;
  transition:
    top,
    font-size 0.1s ease-out;
  color: $input-placeholder-color;
  font-size: font-size('sm');
  font-weight: font-weight('semibold');

  .input-field:-webkit-autofill + &,
  .input-field:focus + &,
  .input-wrapper.active & {
    top: 1.45rem;
    font-size: font-size('3xs');
  }
}

.error-message {
  position: relative;
  z-index: 50;
  color: color('error');
  margin-top: 0.4rem;
  margin-left: 1.7rem;
  font-size: font-size('3xs');
  width: fit-content;
}

.info-icon {
  position: absolute;
  top: 1.5rem;
  right: 1.3rem;
  color: $color-secondary-500;
  cursor: help;
  width: 3ch;
  height: 3ch;
  background: $color-gray-200;
  padding: 1ch;
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;

  &.active {
    color: white;
    background: $color-secondary-500;
  }
}

.info {
  color: $color-secondary-500;
  text-align: center;
  position: absolute;
  z-index: 900;
  right: 0;
  bottom: 0;
  transform: translateY(110%);
  background: $color-gray-200;
  border-radius: $input-border-radius;
  padding: 2ch;
}

.required-text {
  position: absolute;
  top: 100%;
  left: 0;
  color: color('error');
  font-size: font-size('4xs');
  margin-top: 0.2rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  .input-wrapper:hover &,
  .input-wrapper:focus-within & {
    opacity: 1;
  }
}
</style>
